import { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import './App.css';

// Import contexts
import { AuthProvider } from './contexts/AuthContext';
import { GymProvider } from './contexts/GymContext';

// Import layout components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ErrorBoundary from './components/common/ErrorBoundary';

// Import auth components
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Import main components
import Dashboard from './components/dashboard/Dashboard';
import ManagerDashboard from './components/dashboard/ManagerDashboard';
import MarketingMaterials from './components/dashboard/MarketingMaterials';
import BoardView from './components/boards/BoardView';
import MyReservations from './components/reservations/MyReservations';
import { useAuth } from './contexts/AuthContext';

// Import admin components
import AdminPanel from './components/admin/AdminPanel';
import BoardManagement from './components/admin/BoardManagement';
import BoardQRCodes from './components/admin/BoardQRCodes';
import GymManagement from './components/admin/GymManagement';
import UserManagement from './components/admin/UserManagement';
import GymSettings from './components/admin/GymSettings';
import PlatformSettings from './components/admin/PlatformSettings';

// Import admin console (platform admin only)
import AdminConsole from './components/admin-console/AdminConsole';

// Import legal components
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsOfService from './components/legal/TermsOfService';
import ContactUs from './components/legal/ContactUs';

// Import check-in component
import CheckInPage from './components/checkin/CheckInPage';

// Import OAuth components
import OAuthCallback from './components/auth/OAuthCallback';
import OAuthGymSelection from './components/auth/OAuthGymSelection';

// Import settings component
import Settings from './components/settings/Settings';

// Import design system component
import StyleGuide from './components/design/StyleGuide';

// Import onboarding components
import GymProvisioning from './components/admin/GymProvisioning';
import ManagerOnboarding from './components/onboarding/ManagerOnboarding';

// Import QR code quick access
import BoardQuickAccess from './components/boards/BoardQuickAccess';

// Import bug report component
import BugReportButton from './components/common/BugReportButton';

// Auth wrapper component for login/register switching
const AuthWrapper = () => {
  const [isLoginMode, setIsLoginMode] = useState(true);

  if (isLoginMode) {
    return <Login onSwitchToRegister={() => setIsLoginMode(false)} />;
  } else {
    return <Register onSwitchToLogin={() => setIsLoginMode(true)} />;
  }
};

// BoardView wrapper to extract URL params
const BoardViewWrapper = () => {
  const { boardId } = useParams();
  return <BoardView boardId={boardId} />;
};

// BoardQuickAccess wrapper to extract URL params (for QR code access)
const BoardQuickAccessWrapper = () => {
  const { boardId } = useParams();
  return <BoardQuickAccess boardId={boardId} />;
};

// Dashboard wrapper to route based on user role
const DashboardWrapper = () => {
  const { user } = useAuth();

  // Redirect platform_admin to admin console
  if (user?.role === 'platform_admin') {
    return <Navigate to="/admin/console" />;
  }

  // Show ManagerDashboard for gym_manager role
  if (user?.role === 'gym_manager') {
    return <ManagerDashboard />;
  }

  // Show regular Dashboard for all other roles
  return <Dashboard />;
};

// Home redirect wrapper to route based on user role
const HomeRedirect = () => {
  const { user } = useAuth();

  // Redirect platform_admin to admin console
  if (user?.role === 'platform_admin') {
    return <Navigate to="/admin/console" />;
  }

  // All other roles go to dashboard
  return <Navigate to="/dashboard" />;
};

function AppContent() {
  return (
    <ErrorBoundary>
      <div className="App min-h-screen flex flex-col">
        <Router>
          <BugReportButton />
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<AuthWrapper />} />
            <Route path="/register" element={<AuthWrapper />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/checkin/:token" element={<CheckInPage />} />
            <Route path="/style-guide" element={<StyleGuide />} />
            <Route path="/onboard/:token" element={<ManagerOnboarding />} />

            {/* QR Code Quick Access - Public route with optional auth */}
            <Route path="/qr/board/:boardId" element={<BoardQuickAccessWrapper />} />

            {/* OAuth Routes */}
            <Route path="/oauth/callback" element={<OAuthCallback />} />
            <Route path="/oauth/gym-selection" element={<OAuthGymSelection />} />

            {/* Protected Routes */}
            <Route path="/*" element={
              <ProtectedRoute>
                <div className="flex flex-col min-h-screen">
                  <Header />
                  <main className="flex-1">
                    <Routes>
                      {/* Dashboard */}
                      <Route path="/dashboard" element={<DashboardWrapper />} />

                      {/* Board Routes */}
                      <Route path="/board/:boardId" element={<BoardViewWrapper />} />
                      <Route path="/boards" element={<DashboardWrapper />} /> {/* Redirects to dashboard for board selection */}
                      
                      {/* Reservation Routes */}
                      <Route path="/reservations" element={<MyReservations />} />
                      <Route path="/my-reservations" element={<MyReservations />} />

                      {/* Settings Route */}
                      <Route path="/settings" element={<Settings />} />

                      {/* Marketing Materials Route (for gym managers) */}
                      <Route path="/marketing-materials" element={
                        <ProtectedRoute roles={['gym_manager', 'platform_admin', 'chain_owner']}>
                          <MarketingMaterials />
                        </ProtectedRoute>
                      } />

                      {/* Admin Routes */}
                      <Route path="/admin" element={
                        <ProtectedRoute roles={['platform_admin', 'gym_manager', 'chain_owner']}>
                          <AdminPanel />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/console" element={
                        <ProtectedRoute roles={['platform_admin']}>
                          <AdminConsole />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/boards" element={
                        <ProtectedRoute roles={['platform_admin', 'gym_manager', 'chain_owner']}>
                          <BoardManagement />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/board-qr-codes" element={
                        <ProtectedRoute roles={['platform_admin', 'gym_manager', 'chain_owner']}>
                          <BoardQRCodes />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/gyms" element={
                        <ProtectedRoute roles={['platform_admin', 'chain_owner']}>
                          <GymManagement />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/users" element={
                        <ProtectedRoute roles={['platform_admin']}>
                          <UserManagement />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/gym-settings" element={
                        <ProtectedRoute roles={['platform_admin', 'gym_manager', 'chain_owner']}>
                          <GymSettings />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/gym-onboarding" element={
                        <ProtectedRoute roles={['platform_admin']}>
                          <GymProvisioning />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/settings" element={
                        <ProtectedRoute roles={['platform_admin']}>
                          <PlatformSettings />
                        </ProtectedRoute>
                      } />

                      {/* Default redirects */}
                      <Route path="/" element={<HomeRedirect />} />
                      <Route path="*" element={<HomeRedirect />} />
                    </Routes>
                  </main>
                  <Footer />
                </div>
              </ProtectedRoute>
            } />
          </Routes>
        </Router>
      </div>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <AuthProvider>
      <GymProvider>
        <AppContent />
      </GymProvider>
    </AuthProvider>
  );
}

export default App;